import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Container,
  createStyles,
  Dialog,
  DialogActions,
  Divider,
  IconButton,
  makeStyles,
  Slide,
  Theme,
  Typography,
  withStyles,
  TextField
} from "@material-ui/core";
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import { useDispatch, useSelector } from "react-redux";
import { Loading } from '../commonLoadings';
import { createExperience, getExperiences } from "../../../store/actions"
import { TransitionProps } from '@material-ui/core/transitions';
import CloseIcon from '@material-ui/icons/Close';
import MuiDialogContent from '@material-ui/core/DialogContent';
import {
  validateSessionCode
} from "../../../store/actions/creator/creatorExperienceBuilder";
import { notificationFail, displaySuccessMessage } from "../../../store/actions/admin/user";
import { apiClient } from "../../../config/apiClient";
import moment from 'moment';
import { PAGINATION_PER_PAGE_ITEM } from '../../../config/mock';

const styles = (theme: Theme) =>
  createStyles({
    tagmodalheader: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    closeButton: {
      color: theme.palette.grey[500],
      fontFamily: 'roboto-bold',
    },
    closeBtnIcon: {
      color: '#212D4080',
      width: 24,
      height: 24,
      fontFamily: 'roboto-bold'
    },
    modaltitle: {
      fontFamily: 'Spartan-bold',
      fontSize: 24,
      color: '#212D40',
      textAlign: 'left'
    }
  });

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#F0F0F0',
    paddingTop: "30px",
    fontFamily: 'roboto-regular',
    height: '100%',
    overflowX: 'hidden',
  },
  content: {
    minHeight: '100%',
    padding: theme.spacing(4),
    paddingBottom: 100,
    paddingTop: theme.spacing(6),
    '@media (min-width: 2000px)': {
      width: 1600
    },
  },
  dialogWrap: {
    margin: 'auto',
    maxWidth: 500,
    borderRadius: 14,
  },
  actionBtn: {
    textAlign: 'right',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  },
  contentDialog: {
    fontFamily: 'roboto-regular',
    fontSize: 14,
    color: '#697683'
  },
  copyBtn: {
    width: 87,
    height: 37,
    borderRadius: 6,
    background: '#017AEF',
    fontFamily: 'roboto-regular',
    color: '#fff',
    textTransform: 'none',
    boxShadow: 'none',
    fontSize: 14,
    '&:hover': {
      background: '#0f9fef',
      color: '#fff',
    },
  },
  cancelbtn: {
    backgroundColor: '#F0F0F0',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683',
    boxShadow: 'none',
    textTransform: 'none'
  },
  btn: {
    margin: 8
  },
  experienceName: {
    marginTop: '12px',
    fontWeight: 'bold',
    fontFamily: 'roboto-regular',
  },
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DialogTitle = withStyles(styles)((props: any) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.tagmodalheader} {...other}>
      <Typography variant="h5" className={classes.modaltitle}>{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon classes={{ root: classes.closeBtnIcon }} />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2)
  },
}))(MuiDialogContent);

DuplicateExperienceModalDotMenu.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  experienceData: PropTypes.object,
  gotoCopyExperience: PropTypes.func
};

function DuplicateExperienceModalDotMenu({ open, onClose, experienceData }: any) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const loginUserData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : "";
  const apolloClient = useSelector((state: any) => state.apolloClient);
  const [experienceTitle, setExperienceTitle] = useState("");
  const [error, setError] = useState(false);
  const [isLoadingDuplicate, setIsLoadingDuplicate] = useState(false);
  const { page } = useSelector((state: any) => state.experience);

  useEffect(() => {
    if (experienceData) {
      setExperienceTitle(experienceData.name + " (duplicate)")
    }
  }, [experienceData]);

  const onDuplicateExperience = async () => {
    setIsLoadingDuplicate(true)
    if (experienceData && experienceData.session_category && experienceData.session_subcategory) {
        const getOldStimulasData = {
          "requestor": "experience",
          "tenant_id": loginUserData.customer_id
        };
        const tokenres = await apiClient(false).post('/registration/v2/session/tokens', getOldStimulasData);
        if (tokenres && tokenres.data && tokenres.data.token) {
          const checkSession = await validateSessionCode(apolloClient.client, tokenres.data.token);
          if (checkSession && checkSession.data && checkSession.data.validateSessionCode) {
            localStorage.setItem("session_code", String(tokenres.data.token));
          }
        }
        
        try {
          const request = {
            title: experienceTitle,
            description: '',
            scheduled_start: moment(experienceData.start * 1000).toISOString(),
            scheduled_end: moment(experienceData.end * 1000).toISOString(),
            user_id: loginUserData.user_id,
            status: experienceData.session_type === "ASYNC" ? 'COMPLETED' : "UPCOMING",
            type: experienceData.session_type,
            session_code: localStorage.getItem('session_code'),
            participants: [],
            subcategory: experienceData.session_subcategory,
            duplicateExperience: experienceData.session_id,
            session_origin: "DUPLICATE_EXPERIENCE",
          };
      
          if(experienceData.session_type == 'MODERATED'){
            Object.assign(request,{"agenda_json": { schedules: [] }})
          }
      
          dispatch(createExperience(apolloClient.client, request, (res: any) => {
            if (res && res.data.createExperience.status === "true") {
              setTimeout(() => {
                dispatch(displaySuccessMessage(res.data.createExperience.message));
                getExperienceList()
                closeDuplicateModal()
                setIsLoadingDuplicate(false)
              },30000)
            } else {
              dispatch(notificationFail({}, res.data.createExperience.message));
              setIsLoadingDuplicate(false)
            }
          }))
      } catch (e) {
        setIsLoadingDuplicate(false)
      }
    }
  };

  // close modal
  const closeDuplicateModal = () => {
    onClose();
    setError(false)
    setIsLoadingDuplicate(false)
    setExperienceTitle("");
  };

  // experience list get function
  const getExperienceList = () => {
    const dashboard_sort_request = localStorage.getItem("dashboard_sort_request") ? JSON.parse(localStorage.getItem("dashboard_sort_request") || "{}") : "";
    const dashboard_pagination = localStorage.getItem("dashboard_pagination") ? JSON.parse(localStorage.getItem("dashboard_pagination") || "{}") : "";
    
    let sort_column_temp = "";
    let sort_direction_temp = "";


    sort_column_temp = dashboard_sort_request.sort_column;
    sort_direction_temp = dashboard_sort_request.sort_direction;
    const request = {
      user_id: loginUserData.user_id,
      page_no: 1,
      size: PAGINATION_PER_PAGE_ITEM * (dashboard_pagination.page_no ? dashboard_pagination.page_no : page),
      firstpage: true,
      sort_direction: sort_direction_temp,
      sort_column: sort_column_temp
    };
    dispatch(getExperiences(apolloClient.client, request, null));
  };

  const onChangeTitle = (event: any) => {
    if (!event.target.value) {
      setError(true)
    } else {
      setError(false)
    }
    setExperienceTitle(event.target.value);
  };

  return (
    <Dialog
      disableEscapeKeyDown
      open={open}
      TransitionComponent={Transition}
      keepMounted
      fullWidth={true}
      maxWidth={"sm"}
      classes={{
        paper: classes.dialogWrap
      }}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle id="alert-dialog-slide-title" onClose={closeDuplicateModal}>
        {"Duplicate Experience?"}
      </DialogTitle>
      <Divider />
      <DialogContent style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Container maxWidth="md">
          <Typography className={classes.contentDialog}>Are you sure you want to duplicate? Duplicating will create an identical completed experience with all participants and measurements included.<br /></Typography>
          <TextField
            value={experienceTitle}
            fullWidth
            margin="normal"
            id="exp_title"
            label="Experience Title"
            name="exp_title"
            onChange={onChangeTitle}
            error={error}
            helperText={error ? "Experience title is required" : ""}
          />
          <br />
          <Typography className={classes.contentDialog}>This operation cannot be undone.</Typography>
        </Container>
      </DialogContent>
      <DialogActions>
        <Container maxWidth="md" className={classes.actionBtn}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Box mr={2}>
              <Button
                onClick={closeDuplicateModal}
                variant="contained"
                className={classes.cancelbtn}
              >
                Cancel
              </Button>
            </Box>
            <Button
              onClick={onDuplicateExperience}
              variant="contained"
              color="secondary"
              disabled={error}
              className={classes.copyBtn}
            >
              {isLoadingDuplicate ? <Loading style={{ width: 60, height: 30, marginTop: -20 }} /> : 'Duplicate'}
            </Button>
          </Box>
        </Container>
      </DialogActions>
    </Dialog>
  );
}

export default DuplicateExperienceModalDotMenu;
